import React, { useState, useRef } from "react";
import { UploadCloud, X, Loader2 } from "lucide-react";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/** Convert a relative /api/uploads/.. URL to absolute for display, leave external URLs unchanged */
export function resolveImageUrl(url) {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return `${BACKEND_URL}${url}`;
}

async function uploadOne(file, endpoint = "/admin/uploads") {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url; // relative URL
}

/** Single-image drag-drop uploader */
export function ImageUpload({ value, onChange, testid = "image-upload", height = "h-44", endpoint = "/admin/uploads" }) {
    const [over, setOver] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    const handleFiles = async (files) => {
        const file = files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) { setError("Sadece görsel dosyaları"); return; }
        setBusy(true); setError("");
        try {
            const url = await uploadOne(file, endpoint);
            onChange(url);
        } catch (e) {
            setError(e.response?.data?.detail || "Yükleme başarısız");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <div
                data-testid={testid}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setOver(true); }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => { e.preventDefault(); setOver(false); handleFiles(e.dataTransfer.files); }}
                className={`${height} relative rounded-2xl border-2 border-dashed transition-colors cursor-pointer overflow-hidden ${over
                    ? "border-sky-500 bg-sky-50 dark:bg-sky-950/30"
                    : "border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:border-sky-400"}`}
            >
                {value ? (
                    <>
                        <img src={resolveImageUrl(value)} alt="" className="w-full h-full object-cover" />
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); onChange(""); }}
                            className="absolute top-2 right-2 h-8 w-8 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center shadow"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </>
                ) : (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500">
                        {busy ? <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
                            : <UploadCloud className="h-8 w-8 mb-2" />}
                        <p className="text-sm font-medium">{busy ? "Yükleniyor..." : "Görseli sürükle bırak"}</p>
                        {!busy && <p className="text-xs text-slate-400">veya tıkla & seç</p>}
                    </div>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                />
            </div>
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
        </div>
    );
}

/** Multi-image drag-drop uploader (gallery) */
export function GalleryUpload({ value = [], onChange, testid = "gallery-upload", endpoint = "/admin/uploads" }) {
    const [over, setOver] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    const handleFiles = async (files) => {
        if (!files || files.length === 0) return;
        setBusy(true); setError("");
        try {
            const uploaded = [];
            for (const f of Array.from(files)) {
                if (!f.type.startsWith("image/")) continue;
                const url = await uploadOne(f, endpoint);
                uploaded.push(url);
            }
            onChange([...(value || []), ...uploaded]);
        } catch (e) {
            setError(e.response?.data?.detail || "Yükleme başarısız");
        } finally {
            setBusy(false);
        }
    };

    const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

    return (
        <div>
            <div
                data-testid={testid}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setOver(true); }}
                onDragLeave={() => setOver(false)}
                onDrop={(e) => { e.preventDefault(); setOver(false); handleFiles(e.dataTransfer.files); }}
                className={`min-h-[120px] rounded-2xl border-2 border-dashed p-4 flex items-center justify-center cursor-pointer transition-colors ${over
                    ? "border-sky-500 bg-sky-50 dark:bg-sky-950/30"
                    : "border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:border-sky-400"}`}
            >
                <div className="text-center text-slate-500">
                    {busy ? <Loader2 className="h-7 w-7 animate-spin text-sky-500 mx-auto" />
                        : <UploadCloud className="h-7 w-7 mx-auto mb-1" />}
                    <p className="text-sm font-medium">{busy ? "Yükleniyor..." : "Galeri için sürükle bırak"}</p>
                    {!busy && <p className="text-xs text-slate-400">birden fazla dosya seçebilirsiniz</p>}
                </div>
                <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
            </div>
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
            {value.length > 0 && (
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                    {value.map((g, i) => (
                        <div key={i} className="relative group">
                            <img src={resolveImageUrl(g)} alt="" className="w-full h-24 object-cover rounded-lg" />
                            <button
                                type="button"
                                onClick={() => remove(i)}
                                className="absolute top-1 right-1 h-7 w-7 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
